import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import {
  audienceFilters,
  filterThinking,
  formatFilters,
  sourceLabel,
} from "@/features/editorial/filters";
import type { Audience, ContentType } from "@/content/schema";
import { thinking } from "@/lib/content/load";
import { formatDate } from "@/lib/dates";
import { headingText } from "@/lib/heading";

export function ThinkingPage() {
  const [audience, setAudience] = useState<"all" | Audience>("all");
  const [format, setFormat] = useState<"all" | ContentType>("all");
  const records = useMemo(
    () => filterThinking(thinking, audience, format),
    [audience, format],
  );
  const featured = records.filter((item) => item.meta.featured);

  return (
    <>
      <Seo
        title="Thinking"
        description="Essays, product notes, and pieces that started on LinkedIn."
        path="/thinking"
      />
      <section className="wrap section" style={{ paddingTop: "4.5rem" }}>
        <p className="eyebrow">Thinking</p>
        <h1>Notes that have to survive a second reading</h1>
        <p className="lede">
          Essays, product notes, and pieces that started on LinkedIn.
        </p>
        <div className="filter-bar" role="group" aria-label="Audience">
          {audienceFilters.map((item) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={audience === item.id}
              onClick={() => setAudience(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="filter-bar" role="group" aria-label="Format">
          {formatFilters.map((item) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={format === item.id}
              onClick={() => setFormat(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
        {featured.length ? (
          <>
            <p className="section-kicker">Featured</p>
            <div className="grid-2" style={{ marginBottom: "2rem" }}>
              {featured.map((item) => (
                <NoteCard key={item.meta.slug} item={item} />
              ))}
            </div>
          </>
        ) : null}
        <p className="section-kicker">Archive</p>
        <div className="stack">
          {records.map((item) => (
            <NoteCard key={`all-${item.meta.slug}`} item={item} />
          ))}
          {records.length === 0 ? <p>No notes in this filter.</p> : null}
        </div>
      </section>
    </>
  );
}

function NoteCard({
  item,
}: {
  item: (typeof thinking)[number];
}) {
  const linkedin = item.meta.type === "linkedin-highlight";
  return (
    <article className="card">
      <p className="cluster">
        <span className="badge">{sourceLabel(item.meta.type)}</span>
        <span className="meta">
          {linkedin && item.meta.originalPublishedAt
            ? `LinkedIn · ${formatDate(item.meta.originalPublishedAt)}`
            : formatDate(item.meta.sitePublishedAt)}
        </span>
      </p>
      <h2>
        <Link to={item.meta.canonicalPath}>{headingText(item.meta.title)}</Link>
      </h2>
      <p>{item.meta.summary}</p>
      {linkedin && item.meta.sourceUrl ? (
        <p>
          <a href={item.meta.sourceUrl}>On LinkedIn</a>
        </p>
      ) : null}
    </article>
  );
}
